import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import "@/lib/pwaInstall"; // capture beforeinstallprompt ASAP for 1-click install

/* Google Translate compatibility: the translate widget replaces text nodes with <font>
   wrappers, which makes React's removeChild/insertBefore throw during re-renders and
   white-screens the whole app on translated pages. Standard workaround: make the DOM
   operations tolerant of nodes Google has moved. */
if (typeof Node === "function" && Node.prototype) {
  const origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) return child;
    return origRemoveChild.apply(this, arguments);
  };
  const origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return this.appendChild(newNode);
    return origInsertBefore.apply(this, arguments);
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

// Offline Travel Mode: cache the app shell so trips open without internet.
// Auto-update: re-check the service worker on load, when the app regains focus
// and every 30 min, and reload once when a new version takes control — so
// installed PWAs never stay stuck on an old cached build.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/offline-sw.js", { updateViaCache: "none" })
      .then((reg) => {
        const check = () => reg.update().catch(() => {});
        setInterval(check, 30 * 60 * 1000);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
      })
      .catch(() => {});

    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // Only reload when a PREVIOUS worker was in control (i.e. a real update).
      // On a first visit the new SW claiming control must not wipe in-flight UI
      // state (e.g. a half-typed login form).
      if (!hadController || reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

root.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
);
